import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AutoCarousel.style.css";
import MUIButton from "../MUIButton/MUIButton";

// Responsive Auto Carousel for two slides (Shloka & Quote)
// items: [{ id, type: 'shloka'|'quote', text, meaning?, author?, imageUrl?, gradient? }]
// height: css height string
// intervalMs: autoplay interval
// showDots: show pagination dots
// useGradientFirst: prefer gradient when provided

const AutoCarousel = ({ items, height = "56vh", intervalMs = 6000, showDots = true, useGradientFirst = true, onReadNow }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const slides = useMemo(() => items || [], [items]);

  // Autoplay with pause on hover - only left to right
  useEffect(() => {
    if (!containerRef.current || slides.length <= 1) return;

    let isHovered = false;
    const el = containerRef.current;

    const onEnter = () => (isHovered = true);
    const onLeave = () => (isHovered = false);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);

    const timer = setInterval(() => {
      if (isHovered) return;
      // Only advance forward (left to right), don't cycle back
      const next = activeIndex + 1;
      if (next < slides.length) {
        setActiveIndex(next);
        el.scrollTo({ left: el.clientWidth * next, behavior: "smooth" });
      } else {
        // Reset to first slide when reaching the end
        setActiveIndex(0);
        el.scrollTo({ left: 0, behavior: "smooth" });
      }
    }, intervalMs);

    return () => {
      clearInterval(timer);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [activeIndex, slides.length, intervalMs]);

  // Sync index on user scroll (drag/trackpad/swipe)
  const handleScroll = (e) => {
    const { scrollLeft, clientWidth } = e.currentTarget;
    const index = Math.round(scrollLeft / clientWidth);
    if (index !== activeIndex) setActiveIndex(index);
  };

  const metaCharLimit = useMemo(() => {
    if (viewportWidth <= 600) return 200;   // mobile & small
    if (viewportWidth <= 900) return 300;   // tablet & mid-size
    return 500;                             // desktop
  }, [viewportWidth]);

  return (
    <div className="auto-carousel" style={{ height }}>
      <div ref={containerRef} className="auto-carousel-track" onScroll={handleScroll}>
        {slides.map((slide, i) => {
          const style = {};
          if (useGradientFirst && slide.gradient) {
            style.background = slide.gradient;
          } else if (slide.imageUrl) {
            style.backgroundImage = `url("${slide.imageUrl}")`;
          } else if (slide.gradient) {
            style.background = slide.gradient;
          }

          const type = (slide.type || "").toLowerCase();
          const isShloka = type === "shloka";
          const isQuote = type === "quote";
          const isMagzine = type === "magzine";

          const title = isShloka ? "Today's Shloka" : isQuote ? "Today's Quote" : isMagzine ? (slide.title || "") : (slide.title || "");
          const text = slide.text || slide.subtitle || "";
          const meta = isShloka ? (slide.meaning || "") : isQuote ? (slide.author ? `— ${slide.author}` : "") : isMagzine ? (slide.description || "") : "";
          const metaLimited = meta && meta.length > metaCharLimit ? meta.slice(0, metaCharLimit).trimEnd() + "..." : meta;

          return (
            <section className="auto-carousel-slide" key={slide.id || i} style={style}>
              <div className="auto-carousel-overlay" />
              <div className={`auto-carousel-content ${isMagzine ? "magzine" : ""}`}>
                {!isMagzine && <div className="quote-mark quote-mark--tl">“</div>}
                <h2 className="auto-carousel-title">{title}</h2>
                {text ? <p className="auto-carousel-text">{text}</p> : null}
                {metaLimited ? <p className="auto-carousel-meta">{metaLimited}</p> : null}
                
                {isMagzine && (
                  <div className="auto-carousel-cta">
                    <MUIButton primary onClick={() => onReadNow?.(slide)}>
                      Read Now
                    </MUIButton>
                  </div>
                )}
                {!isMagzine && <div className="quote-mark quote-mark--br">”</div>}
              </div>
            </section>
          );
        })}
      </div>

      {showDots && (
        <div className="auto-carousel-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === activeIndex ? "active" : ""}`}
              onClick={() => {
                const el = containerRef.current;
                if (!el) return;
                setActiveIndex(i);
                el.scrollTo({ left: el.clientWidth * i, behavior: "smooth" });
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoCarousel;